/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { PillProps } from 'src/core/components/Pill';
import { PillContainer } from 'src/core/components/PillContainer';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { FiltersItem, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

type FiltersPillContainerProps = {
    /**
     * The filters to be rendered.
     */
    filters: FiltersItem[];

    /**
     * A callback fires on removing the filters.
     */
    onRemoveFilter: (filter: SelectionChange) => void;
};

const messages = defineMessages({
    differenceFilterType: {
        id: 'utterancesFilter.filtersBlade.differenceFilterType',
        defaultMessage: 'Difference'
    },
    entityFilterType: {
        id: 'utterancesFilter.filtersBlade.entityFilterType',
        defaultMessage: 'Entity'
    },
    intentFilterType: {
        id: 'utterancesFilter.filtersBlade.intentFilterType',
        defaultMessage: 'Intent'
    },
    correctFilterName: {
        id: 'utterancesFilter.filtersBlade.correctFilterName',
        defaultMessage: 'Correct'
    },
    incorrectFilterName: {
        id: 'utterancesFilter.filtersBlade.incorrectFilterName',
        defaultMessage: 'Incorrect'
    },
    unclearFilterName: {
        id: 'utterancesFilter.filtersBlade.unclearFilterName',
        defaultMessage: 'Unclear'
    }
});

const filtersTheme = getScopedTheme('Filters');

/**
 * Renders a filter pill for the given filter type.
 */
export const FiltersPillContainer = (props: FiltersPillContainerProps) => {
    const { filters, onRemoveFilter } = props;
    const { localize } = useLocalization();
    const [showInputPill, setShowInputPill] = React.useState(false);

    const existingFilterPillsProps = filters.map<PillProps>((filter, index) => {
        switch (filter.id) {
            case 'correct':
                return {
                    index,
                    name: 'Correct',
                    pillKey: 'correct',
                    onRemovePill: () => onRemoveFilter({ args: filter, changeType: 'difference' }),
                    onRenderPillContent: () => (
                        <FilterPillContent
                            filterName={localize(messages.correctFilterName)}
                            filterType={localize(messages.differenceFilterType)}
                        />
                    )
                };
            case 'incorrect':
                return {
                    index,
                    name: 'Incorrect',
                    pillKey: 'incorrect',
                    onRemovePill: () => onRemoveFilter({ args: filter, changeType: 'difference' }),
                    onRenderPillContent: () => (
                        <FilterPillContent
                            iconName="SkypeCircleMinus"
                            iconColor={filtersTheme.incorrectColor}
                            filterName={localize(messages.incorrectFilterName)}
                            filterType={localize(messages.differenceFilterType)}
                        />
                    )
                };
            case 'unclear':
                return {
                    index,
                    name: 'Unclear',
                    pillKey: 'unclear',
                    onRemovePill: () => onRemoveFilter({ args: filter, changeType: 'difference' }),
                    onRenderPillContent: () => (
                        <FilterPillContent
                            iconName="IncidentTriangle"
                            iconColor={filtersTheme.unclearColor}
                            filterName={localize(messages.unclearFilterName)}
                            filterType={localize(messages.differenceFilterType)}
                        />
                    )
                };
            default:
                switch (filter.filterType) {
                    case 'intents':
                        return {
                            index,
                            name: filter.name,
                            pillKey: filter.id,
                            onRemovePill: () => onRemoveFilter({ args: filter, changeType: 'intents' }),
                            onRenderPillContent: () => (
                                <FilterPillContent filterName={filter.name} filterType={localize(messages.intentFilterType)} />
                            )
                        };
                    default:
                        return {
                            index,
                            name: filter.name,
                            pillKey: filter.id,
                            onRemovePill: () => onRemoveFilter({ args: filter, changeType: 'entities' }),
                            onRenderPillContent: () => (
                                <FilterPillContent filterName={filter.name} filterType={localize(messages.entityFilterType)} />
                            )
                        };
                }
        }
    });

    return (
        <PillContainer
            allowAddPills={false}
            pills={existingFilterPillsProps}
            onAddNewPill={noop}
            showInputPill={showInputPill}
            onRemovePill={() => setShowInputPill(false)}
            onAddIconClick={() => setShowInputPill(true)}
        />
    );
};

type FilterPillContentProps = {
    filterType: string;
    filterName: string;
    iconName?: string;
    iconColor?: string;
};

const FilterPillContent = ({ filterType, iconName, filterName, iconColor }: FilterPillContentProps) => (
    <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5 }}>
        <LuisText>{filterType}:</LuisText>

        {iconName && <Icon iconName={iconName} styles={{ root: { color: iconColor } }} />}

        <LuisText fontWeight={filtersTheme.boldFontWeight as Property.FontWeight}>{filterName}</LuisText>
    </Stack>
);
